import type { Cell } from "@silevis/reactgrid";
import { NonEditableCell, TextCell } from "@silevis/reactgrid";
import type { Dayjs } from "dayjs";
import { range, sortBy, uniq } from "lodash";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import DateCell from "./DateCell";
import { getCitizenshipSelectAlpha2Codes } from "./isoCountryCodes";
import type {
  ParticipantListCombinationPerson,
  ParticipantListCombinationResponse,
  ParticipantListCombinationRoom,
  ParticipantListRequiredFields,
  ParticipantListTemplateCombination,
  TemplateRoom,
} from "./participantListApi";
import { normalizeCitizenship, normalizeGender } from "./participantListPasteAliases";
import SelectCell from "./SelectCell";
import { styleIfDifferent } from "./styleIfDifferent";

type PersonType = "PARTICIPANT" | "DRIVER" | "TOUR_LEADER";

export interface Person {
  readonly type: PersonType;
  readonly name: string;
  readonly surname: string;
  readonly dateOfBirth: string | undefined;
  readonly gender: string | undefined;
  readonly citizenship: string | undefined;
  readonly documentNumber: string | undefined;
  readonly documentExpiryDate: string | undefined;
}

export interface Room {
  readonly people: (Person | null)[];
}

export interface ParticipantsForCombination {
  [numberOfPeople: string]: {
    readonly rooms: Room[];
  };
}

const cabinDividerStyle = { borderTop: "2px solid #f0f0f4" };

export function getRoomsCells(
  { rooms }: ParticipantListTemplateCombination,
  requiredFields: ParticipantListRequiredFields,
  participants: ParticipantsForCombination,
  previousParticipants: ParticipantListCombinationResponse | undefined,
  onParticipantsChange: (roomType: string, roomIdx: number, personIdx: number, update: Partial<Person> | null) => void,
): Cell[] {
  const maxColumnsCount = 6 + Object.values(requiredFields).filter((v) => v === true).length;
  const roomOffsets: Record<string, number> = {};
  return sortBy(rooms, (r) => r.numberOfPeople)
    .flatMap((r) => {
      const offset = roomOffsets[`${r.numberOfPeople}`] ?? 0;
      roomOffsets[`${r.numberOfPeople}`] = offset + r.count;
      return roomRowsForRoomType(
        r,
        requiredFields,
        participants,
        previousParticipants,
        offset,
        (roomIdx, personIdx, p) => onParticipantsChange(`${r.numberOfPeople}`, offset + roomIdx, personIdx, p),
      );
    })
    .flatMap((peopleRows, idx) => peopleRows.map((r) => ({ row: r, roomNumber: idx + 1 })))
    .flatMap(({ row, roomNumber }, idx) => {
      if (row.length === maxColumnsCount) {
        const cabinBorderStyle = roomNumber > 1 ? cabinDividerStyle : undefined;
        const withBorder = (cell: Omit<Cell, "rowIndex">): Omit<Cell, "rowIndex"> =>
          cabinBorderStyle
            ? { ...cell, props: { ...cell.props, style: { ...cell.props?.style, ...cabinBorderStyle } } }
            : cell;
        const [roomNumberCell, roomTypeCell, ordinalCell, ...personCellsRow] = row;
        return [
          {
            ...roomNumberCell,
            rowIndex: idx + 1,
            props: { value: roomNumber, style: cabinBorderStyle },
          },
          { ...withBorder(roomTypeCell), rowIndex: idx + 1 },
          {
            ...ordinalCell,
            rowIndex: idx + 1,
            props: { value: idx + 1, style: cabinBorderStyle },
          },
          ...personCellsRow.map((c) => ({ ...withBorder(c), rowIndex: idx + 1 })),
        ];
      }
      return [
        { rowIndex: idx + 1, colIndex: 2, Template: NonEditableCell, props: { value: idx + 1 } },
        ...row.map((c) => ({ ...c, rowIndex: idx + 1 })),
      ];
    });
}

function roomRowsForRoomType(
  templateRoom: TemplateRoom,
  requiredFields: ParticipantListRequiredFields,
  participants: ParticipantsForCombination,
  previousParticipants: ParticipantListCombinationResponse | undefined,
  offset: number,
  onChange: (roomIdx: number, personIdx: number, person: Partial<Person> | null) => void,
): Omit<Cell, "rowIndex">[][][] {
  const participantsForRoomType = participants[`${templateRoom.numberOfPeople}`];
  const showDiffs = previousParticipants !== undefined;
  const previousParticipantsForRoomType = previousParticipants
    ? previousParticipants[`${templateRoom.numberOfPeople}`]
    : undefined;
  const emptyRoom: Room = { people: range(0, templateRoom.numberOfPeople).map(() => null) };
  const rooms = range(0, templateRoom.count).map((roomIdx) => ({
    current: participantsForRoomType?.rooms[offset + roomIdx] ?? emptyRoom,
    previous: previousParticipantsForRoomType?.rooms[offset + roomIdx],
  }));
  return rooms.map(({ current, previous }, roomIdx) =>
    peopleRowsForRoom(templateRoom, requiredFields, current, previous, showDiffs, (personIdx, p) =>
      onChange(roomIdx, personIdx, p),
    ),
  );
}

function peopleRowsForRoom(
  templateRoom: TemplateRoom,
  requiredFields: ParticipantListRequiredFields,
  room: Room,
  previousRoom: ParticipantListCombinationRoom | undefined,
  showDiffs: boolean,
  onChange: (personIdx: number, person: Partial<Person> | null) => void,
): Omit<Cell, "rowIndex">[][] {
  const people = range(0, templateRoom.numberOfPeople).map((idx) => ({
    current: room.people[idx],
    previous: previousRoom?.people[idx],
  }));
  const firstRow = [
    {
      colIndex: 0,
      rowSpan: templateRoom.numberOfPeople,
      Template: NonEditableCell,
    },
    {
      colIndex: 1,
      Template: NonEditableCell,
      rowSpan: templateRoom.numberOfPeople,
      props: { value: uniq(templateRoom.resources.map((r) => r.option.name)).join("\n") },
    },
    {
      colIndex: 2,
      Template: NonEditableCell,
    },
    ...personCells(people[0].current, people[0].previous, showDiffs, requiredFields, (t) => onChange(0, t)),
  ];
  const otherRows = range(1, templateRoom.numberOfPeople).map((personIdx) =>
    personCells(people[personIdx].current, people[personIdx].previous, showDiffs, requiredFields, (t) =>
      onChange(personIdx, t),
    ),
  );
  return [firstRow, ...otherRows];
}

function personCells(
  person: Person | null,
  previousPerson: ParticipantListCombinationPerson | null | undefined,
  showDiffs: boolean,
  requiredFields: ParticipantListRequiredFields,
  onChange: (person: Partial<Person> | null) => void,
) {
  return [
    (t: Person | null) => ({
      Template: SelectCell,
      props: {
        value: t ? t.type : "EMPTY",
        style: styleIfDifferent(
          t ? t.type : "EMPTY",
          previousPerson === undefined ? "PARTICIPANT" : previousPerson === null ? "EMPTY" : previousPerson.type,
          showDiffs,
        ),
        onValueChanged: (v: PersonType | "EMPTY") => onChange(v === "EMPTY" ? null : { type: v }),
        values: [
          { label: "Tourist", value: "PARTICIPANT" },
          { label: "Driver", value: "DRIVER" },
          { label: "Tour leader", value: "TOUR_LEADER" },
          { label: "Unused bed", value: "EMPTY" },
        ],
      },
    }),
    cellForPerson((t) => ({
      Template: TextCell,
      props: {
        text: t.name,
        style: styleIfDifferent(t.name, previousPerson?.name, showDiffs),
        onTextChanged: (v: string) => onChange({ name: v }),
      },
    })),
    cellForPerson((t) => ({
      Template: TextCell,
      props: {
        text: t.surname,
        style: styleIfDifferent(t.surname, previousPerson?.surname, showDiffs),
        onTextChanged: (v: string) => onChange({ surname: v }),
      },
    })),
    cellForPerson(
      (t) => ({
        Template: DateCell,
        props: {
          value: parseIsoDate(t.dateOfBirth),
          style: styleIfDifferent(t.dateOfBirth, previousPerson?.dateOfBirth, showDiffs),
          onValueChanged: (v: Dayjs | null | undefined) => onChange({ dateOfBirth: v?.format(ISO_DATE_FORMAT) }),
        },
      }),
      requiredFields.dateOfBirth,
    ),
    cellForPerson(
      (t) => ({
        Template: SelectCell,
        props: {
          value: t.gender,
          style: styleIfDifferent(t.gender, previousPerson?.gender, showDiffs),
          onValueChanged: (v: string | null | undefined) => onChange({ gender: v ? v : undefined }),
          normalizePasted: normalizeGender,
          values: [
            { label: "", value: undefined },
            { label: "F", value: "FEMALE" },
            { label: "M", value: "MALE" },
          ],
        },
      }),
      requiredFields.gender,
    ),
    cellForPerson(
      (t) => ({
        Template: SelectCell,
        props: {
          value: t.citizenship,
          style: styleIfDifferent(t.citizenship, previousPerson?.citizenship, showDiffs),
          onValueChanged: (v: string | null | undefined) => onChange({ citizenship: v ?? undefined }),
          normalizePasted: normalizeCitizenship,
          values: [
            { label: "", value: undefined },
            ...getCitizenshipSelectAlpha2Codes().map((alpha2) => ({ label: alpha2, value: alpha2 })),
          ],
        },
      }),
      requiredFields.citizenship,
    ),
    cellForPerson(
      (t) => ({
        Template: TextCell,
        props: {
          text: t.documentNumber,
          style: styleIfDifferent(t.documentNumber, previousPerson?.documentNumber, showDiffs),
          onTextChanged: (v: string) => onChange({ documentNumber: v }),
        },
      }),
      requiredFields.documentNumber,
    ),
    cellForPerson(
      (t) => ({
        Template: DateCell,
        props: {
          value: parseIsoDate(t.documentExpiryDate),
          style: styleIfDifferent(t.documentExpiryDate, previousPerson?.documentExpiryDate, showDiffs),
          onValueChanged: (v: Dayjs | null | undefined) => onChange({ documentExpiryDate: v?.format(ISO_DATE_FORMAT) }),
        },
      }),
      requiredFields.documentExpiryDate,
    ),
  ]
    .filter((v) => !!v)
    .map((f) => f(person))
    .map((c, idx) => ({ ...c, colIndex: idx + 3 }));
}

export function parseIsoDate(date: string | null | undefined): Dayjs | undefined {
  return date ? dayjs.utc(date, ISO_DATE_FORMAT) : undefined;
}

function cellForPerson(
  f: (t: Person) => Omit<Cell, "rowIndex" | "colIndex">,
  isRequired = true,
): ((t: Person | null) => Omit<Cell, "rowIndex" | "colIndex">) | null {
  if (!isRequired) {
    return null;
  }
  return (person: Person | null) => {
    if (person) {
      return f(person);
    }
    return emptyParticipantCell;
  };
}

export const emptyParticipantCell = {
  Template: NonEditableCell,
  props: {
    value: "",
    style: {
      background: "rgba(240,240,240,0.8)",
    },
  },
};
